import { supabase } from "@/lib/supabase";

export interface ConfirmationEmailData {
  email: string;
  name: string;
  confirmationType: "membership" | "event_registration" | "contact_form" | "mailing_list";
  details?: Record<string, any>;
}

export interface EventReminderData {
  email: string;
  name: string;
  eventDetails: {
    eventName: string;
    date: string;
    time: string;
    location: string;
    course: string;
    updateMessage?: string;
    newDate?: string;
    newTime?: string;
    newLocation?: string;
  };
  reminderType: "24_hours" | "1_week" | "last_minute";
}

export interface NewsletterData {
  recipients: Array<{ email: string; name: string }>;
  subject: string;
  content: {
    introduction?: string;
    upcomingEvents?: Array<{
      name: string;
      date: string;
      course: string;
    }>;
    courseReviews?: Array<{
      courseName: string;
      snippet: string;
      rating: number;
    }>;
    memberSpotlight?: {
      name: string;
      achievement: string;
    };
    announcements?: string[];
    closing?: string;
  };
  newsletterType?: string;
}

class EmailService {
  /**
   * Send confirmation email
   */
  async sendConfirmationEmail(data: ConfirmationEmailData): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { data: result, error } = await supabase.functions.invoke(
        "send-confirmation-email",
        {
          body: data
        }
      );

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error: any) {
      console.error("Error sending confirmation email:", error);
      return {
        success: false,
        error: error.message || "Failed to send confirmation email"
      };
    }
  }

  /**
   * Send event reminder
   */
  async sendEventReminder(data: EventReminderData): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { data: result, error } = await supabase.functions.invoke(
        "send-event-reminder",
        {
          body: data
        }
      );

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error: any) {
      console.error("Error sending event reminder:", error);
      return {
        success: false,
        error: error.message || "Failed to send event reminder"
      };
    }
  }

  /**
   * Send newsletter to multiple recipients
   */
  async sendNewsletter(data: NewsletterData): Promise<{
    success: boolean;
    totalRecipients?: number;
    successful?: number;
    failed?: number;
    error?: string;
  }> {
    try {
      const { data: result, error } = await supabase.functions.invoke("send-newsletter", {
        body: data
      });

      if (error) {
        throw error;
      }

      return {
        success: true,
        totalRecipients: result.totalRecipients,
        successful: result.successful,
        failed: result.failed
      };
    } catch (error: any) {
      console.error("Error sending newsletter:", error);
      return {
        success: false,
        error: error.message || "Failed to send newsletter"
      };
    }
  }

  /**
   * Send bulk event reminders (24 hours before)
   */
  async sendBulkEventReminders(
    eventId: string,
    eventDetails: EventReminderData["eventDetails"],
    reminderType: EventReminderData["reminderType"] = "24_hours"
  ): Promise<{
    success: boolean;
    sent?: number;
    failed?: number;
    error?: string;
  }> {
    try {
      // Fetch all registered members for the event
      // This would typically come from your events registration table
      // For now, returning placeholder structure
      const registeredMembers: Array<{ email: string; name: string }> = [];

      let sent = 0;
      let failed = 0;

      for (const member of registeredMembers) {
        const result = await this.sendEventReminder({
          email: member.email,
          name: member.name,
          eventDetails,
          reminderType
        });

        if (result.success) {
          sent++;
        } else {
          failed++;
        }
      }

      return {
        success: true,
        sent,
        failed
      };
    } catch (error: any) {
      console.error("Error sending bulk event reminders:", error);
      return {
        success: false,
        error: error.message || "Failed to send bulk reminders"
      };
    }
  }

  /**
   * Send membership application confirmation
   */
  async sendMembershipConfirmation(email: string, name: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    return this.sendConfirmationEmail({
      email,
      name,
      confirmationType: "membership"
    });
  }

  /**
   * Send event registration confirmation
   */
  async sendEventRegistrationConfirmation(
    email: string,
    name: string,
    eventDetails: Record<string, any>
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    return this.sendConfirmationEmail({
      email,
      name,
      confirmationType: "event_registration",
      details: eventDetails
    });
  }

  /**
   * Send contact form acknowledgment
   */
  async sendContactFormAcknowledgment(
    email: string,
    name: string,
    message: string
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    return this.sendConfirmationEmail({
      email,
      name,
      confirmationType: "contact_form",
      details: { message }
    });
  }

  /**
   * Send mailing list welcome email
   */
  async sendMailingListWelcome(email: string, name: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    return this.sendConfirmationEmail({
      email,
      name,
      confirmationType: "mailing_list"
    });
  }
}

export const emailService = new EmailService();

export const welcomeEmailService = {
  // Send welcome email with login credentials
  async sendWelcomeEmail(
    email: string,
    fullName: string,
    password: string
  ): Promise<{ success: boolean; error: Error | null; message?: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('send-welcome-email', {
        body: {
          email,
          fullName,
          password
        }
      });

      if (error) {
        throw new Error(`Failed to send welcome email: ${error.message}`);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return {
        success: true,
        error: null,
        message: 'Welcome email sent successfully'
      };
    } catch (error) {
      console.log('Welcome email error:', error);
      return {
        success: false,
        error: error as Error,
        message: error instanceof Error ? error.message : 'Failed to send welcome email'
      };
    }
  }
};